import { toast } from 'sonner';
import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useAuthUserRestaurant,
  useGetProductOffersQuery,
  useReorderProductOffersMutation,
} from '@durma-soft/gros-sdk';
import { ProductOffer } from '@durma-soft/gros-sdk/dist/types/product-offers';

import { Link } from '@/components/shared/link';
import { DataTable } from '@/components/shared/data-table';
import { DataTableDnDContext } from '@/components/shared/data-table-dnd-context';
import { buttonVariants } from '@/components/shared/shadcn-ui/button';
import { CreateProductOfferModal } from '@/components/restaurant/product-offers/create-product-offer-modal';
import { EditProductOfferModal } from '@/components/restaurant/product-offers/edit-product-offer-modal';

import { manageOfferColumns } from '@/config/restaurant/platform-product-offers';

const PlatformProductOffers = () => {
  const { productOfferId } = useParams();
  const { t } = useTranslation();
  const restaurant = useAuthUserRestaurant();

  const [orderedOffers, setOrderedOffers] = useState<ProductOffer[]>([]);

  const offers = useGetProductOffersQuery({ restaurant_id: restaurant.id });

  useEffect(() => {
    setOrderedOffers(offers.data ?? []);
  }, [offers.data]);

  const dataIds = useMemo(
    () => orderedOffers.map((offer) => `${offer.id}`),
    [orderedOffers],
  );

  const reorderOffers = useReorderProductOffersMutation();

  const handleReorder = async (newOffers: ProductOffer[]) => {
    setOrderedOffers(newOffers);
    try {
      await reorderOffers.mutateAsync({
        restaurant_id: restaurant.id,
        product_offer_ids: newOffers.map((offer) => offer.id),
      });
    } catch {
      toast.error(t('product-offers.reorder-error-msg'));
    }
  };

  const memoizedOfferColumns = useMemo(() => manageOfferColumns(t), [t]);

  return (
    <>
      <Helmet>
        <title>{t('product-offers.title')}</title>
      </Helmet>
      <div className="flex flex-col gap-8">
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <h1 className="text-lg font-semibold md:text-2xl">
              {t('product-offers.title')}
            </h1>
            <Link
              to="/restaurant/platform/product-offers/create"
              className={buttonVariants({ variant: 'default' })}
            >
              {t('product-offers.create')}
            </Link>
          </div>
          <DataTableDnDContext
            key={dataIds.join()}
            dataIds={dataIds}
            data={orderedOffers}
            setData={(newOffers) => handleReorder(newOffers as ProductOffer[])}
          >
            <DataTable
              draggableRows
              dataIds={dataIds}
              columns={memoizedOfferColumns}
              data={orderedOffers}
              isLoading={offers.isLoading}
              getRowId={(row) => `${row.id}`}
            />
          </DataTableDnDContext>
        </div>
      </div>
      <CreateProductOfferModal />
      {Boolean(productOfferId) && (
        <EditProductOfferModal key={productOfferId} />
      )}
    </>
  );
};

export default PlatformProductOffers;
